import { Box } from "@qws-org/ui-kit";
import type { FC, PropsWithChildren } from "react";
import { useEffect, useRef, useState } from "react";

import { useClientContext } from "~/contexts/client-context";
import type { Pages } from "~/core";
import type { NotificationData } from "~/core/services/notifications.service";
import { isWelcomeOfferNotification } from "~/features/welcome-offer-notification/isWelcomeOfferNotification";
import { WelcomeOfferNotification } from "~/features/welcome-offer-notification/WelcomeOfferNotification";
import { parseConditionTemplate } from "~/shared/utils/parse-condition-template";
import { evaluateCmsExpression } from "~/shared/utils/parse-string-expression-to-boolean";
import { useAction } from "~/widgets/use-action";

export type NotificationsConstructorProps = {
  data: NotificationData;
};
const STORAGE_KEY = `NOTIFICATION_REPEAT_LEFT_COUNT`;
export const NotificationConstructor: FC<
  PropsWithChildren<NotificationsConstructorProps>
> = ({ children, data }) => {
  const { authToken, isNewUser } = useClientContext();
  const component = data?.translations?.[0]?.components?.[0]?.item;
  const delay = (data?.translations?.[0]?.delay_time ?? 0) * 1000;
  const duration = (data?.translations?.[0]?.duration_time ?? 0) * 1000;
  const isRepeatable = data?.translations?.[0]?.repeatable ?? false;
  const repeatCount = data?.translations?.[0]?.repeat_count ?? 0;
  // итоговое количество показов за сессию
  const totalCountOfRepeat = 1 + (isRepeatable ? repeatCount : 0);

  // выражение из CMS
  const parsedCondition = evaluateCmsExpression(
    parseConditionTemplate(data?.translations?.[0]?.condition_v2 ?? "", {
      AUTH_TOKEN: String(!!authToken),
      IS_NEW_USER: String(isNewUser),
    }),
  );
  const [visible, setVisible] = useState(false);
  const [remainingShows, setRemainingShows] =
    useState<number>(totalCountOfRepeat);

  const remainingShowsRef = useRef(totalCountOfRepeat);
  const shownTimesRef = useRef(0);

  const onClose = (): void => {
    setVisible(false);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    // количество оставшихся показов в СС
    const storedRemainCount = sessionStorage.getItem(STORAGE_KEY);
    // если уже есть, используем его
    if (storedRemainCount !== null) {
      remainingShowsRef.current = Number(storedRemainCount);
      setRemainingShows(Number(storedRemainCount));
    } else {
      remainingShowsRef.current = totalCountOfRepeat;
      sessionStorage.setItem(STORAGE_KEY, String(totalCountOfRepeat));
      setRemainingShows(totalCountOfRepeat);
    }
  }, [STORAGE_KEY, totalCountOfRepeat]);

  useEffect(() => {
    if (typeof window === "undefined" || !component) return;

    let showTimer: ReturnType<typeof setTimeout> | null = null;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;

    const showNotification = (): void => {
      if (remainingShowsRef.current <= 0) return;

      setVisible(true);
      // счётчик показов в текущей сессии
      shownTimesRef.current += 1;
      // уменьшить оставшиеся показы
      remainingShowsRef.current -= 1;

      sessionStorage.setItem(STORAGE_KEY, String(remainingShowsRef.current));

      setRemainingShows(remainingShowsRef.current);

      if (duration > 0) {
        hideTimer = setTimeout(() => {
          setVisible(false);

          // если нотификация повторяемая и количество показов НЕ достигло лимита totalCountOfRepeat
          if (isRepeatable && shownTimesRef.current < totalCountOfRepeat) {
            showTimer = setTimeout(showNotification, delay);
          }
        }, duration);
      }
    };

    showTimer = setTimeout(showNotification, delay);

    return () => {
      if (showTimer) clearTimeout(showTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [
    component,
    delay,
    duration,
    isRepeatable,
    totalCountOfRepeat,
    STORAGE_KEY,
  ]);

  const getUrl = (detailedPage: Pages): string => {
    if (!detailedPage?.slug) return `/`;

    const { segment, slug } = detailedPage;
    return segment ? `/${segment}/${slug}` : `/${slug}`;
  };

  const { callAction: onCallAction, isActionInactive } = useAction();

  return component ? (
    <Box position={{ type: "relative" }} height="100%">
      {visible &&
        parsedCondition &&
        remainingShows >= 0 &&
        (() => {
          if (isWelcomeOfferNotification(component)) {
            return (
              <WelcomeOfferNotification
                data={component}
                getUrl={getUrl}
                onCallAction={onCallAction}
                isControlInactive={isActionInactive}
                onClose={onClose}
              />
            );
          }
          return null;
        })()}

      {children}
    </Box>
  ) : (
    <>{children}</>
  );
};
